import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import AppContext from "../../../../appContext";
import AppButton from "../../../../shared/components/generic/button/app-button";
import FormGeneric from "../../../../shared/components/generic/form/form-generic";
import InnerPageStructure from "../../../../shared/components/generic/inner-page-structure/inner-page-structure";
import PageStateMessage from "../../../../shared/components/generic/page-state-message/page-state-message";
import PageSteps from "../../../../shared/components/generic/page-steps/page-steps";
import { TokenUtils } from "../../../../shared/utils/TokenUtils";
import { inputsConfig } from "../../config/simulacao/inputs-config";
import { simulationSteps } from "../../config/simulacao/steps-config";
import { useClientByCnpj } from "../../hooks/useClientByCnpj";
import { useFinancialInstallments } from "../../hooks/useFinanciaInstallments";
import { useFinancialIndicatedConditions } from "../../hooks/useFinancialIndicatedConditions";
import { useSimulation } from "../../hooks/useSimulation";
import { useSimulationById } from "../../hooks/useSimulationById";
import { useSimulationUpdate } from "../../hooks/useSimulationUpdate";
import {
	ALLOWED_SIMULATION_MODES,
	calculateBuybackInstallmentValue,
	formatCalculatedInstallmentsByPaymentCondition,
	getCurrencyNumber,
	getInitialPaymentFields,
	getSavedSimulationMode,
	isCalculateSubmitDisabled,
	clearSimulationMode,
	saveSimulationMode,
} from "../../utils/simulacao-utils";
import SimulationSummary from "../../components/simulation-summary/simulation-summary";
import { summaryConfig } from "../../config/simulacao/summary-config";
import { showCommonAlert } from "../../../../shared/components/generic/alert/utils/show-common-alert-utils";
import SimulationErrorAlerts from "../../components/simulation-error-alerts/simulation-error-alerts";
import { removeClearedValidationErrors } from "../../../utils/validation-utils";
import PageLoading from "../../../../shared/components/generic/page-loading/page-loading";
import { showClientWithoutSalesHistoryAlert } from "../../components/client-without-sales-history-alert/client-without-sales-history-alert";
import "../simulacao.css";

const getPersistableSimulationFormValues = values => {
	if (!values) {
		return null;
	}

	const persistableValues = {
		clientDocument: values.clientDocument,
		clientName: values.clientName,
		equipmentName: values.equipmentName,
		equipmentValue: values.equipmentValue,
		installments: values.installments,
		paymentConditions: values.paymentConditions,
		buyback: values.buyback,
		buybackEquipment: values.buybackEquipment,
		buybackValue: values.buybackValue
	};

	return Object.entries(persistableValues).reduce((acc, [key, value]) => {
		if (value !== undefined) {
			acc[key] = value;
		}

		return acc;
	}, {});
};

const Simulacao = () => {
	const context = useContext(AppContext);
	const location = useLocation();
	const history = useHistory();

	const [hasModuleAccess, setHasModuleAccess] = useState(false);
	const [validationErrors] = useState({});
	const [calculationResult, setCalculationResult] = useState(null);
	const [createdSimulationId, setCreatedSimulationId] = useState(null);
	const [indicatedConditions, setIndicatedConditions] = useState(null);
	const [financialConditionParams, setFinancialConditionParams] = useState(null);
	const [isRefreshingPaymentConditions, setIsRefreshingPaymentConditions] = useState(false);
	const [selectedPaymentConditionId, setSelectedPaymentConditionId] = useState(null);
	const [formValues, setFormValues] = useState(null);
	const [restoredSimulationData, setRestoredSimulationData] = useState(null);
	const [isRestoringSimulationState, setIsRestoringSimulationState] = useState(
		Boolean(location.state?.restoreSimulationState)
	);
	const [shouldClearFieldsAfterCnpjChange, setShouldClearFieldsAfterCnpjChange] = useState(false);
	const [clearedValidationErrors, setClearedValidationErrors] = useState({});
	const [simulationActionLoadingMessage, setSimulationActionLoadingMessage] = useState("");

	const lastIndicatedConditionsParamsRef = useRef(null);
	const restoredSimulationStateRef = useRef(false);
	const calculationSectionRef = useRef(null);
	const shouldFocusCalculationRef = useRef(false);

	const { id: routeSimulationId } = useParams();

	const storageSimulationId = routeSimulationId || "create";

	const requestedMode =
		location.state?.mode ||
		location.state?.restoreSimulationState?.mode ||
		getSavedSimulationMode(storageSimulationId);

	const fallbackMode = routeSimulationId ? "view" : "create";

	const mode = ALLOWED_SIMULATION_MODES.includes(requestedMode)
		? requestedMode
		: fallbackMode;

	const isViewMode = mode === "view";
	const isEditMode = mode === "edit";
	const shouldSearchCnpj = mode === "create" || mode === "edit";

	const hasRestoreSimulationState = Boolean(
		location.state?.restoreSimulationState
	);

	const {
		simulation,
		loading: simulationLoading,
		error
	} = useSimulationById({
		enabled: !hasRestoreSimulationState && (isViewMode || isEditMode)
	});

	const {
		installments: financialInstallments,
		loading: financialInstallmentsLoading,
		error: financialInstallmentsError,
		retryCount: financialInstallmentsRetryCount,
		maxRetries: financialInstallmentsMaxRetries
	} = useFinancialInstallments();

	const {
		loading: clientByCnpjLoading,
		searchClientByCnpj
	} = useClientByCnpj();

	const {
		loading: indicatedConditionsLoading,
		error: indicatedConditionsError,
		fetchFinancialIndicatedConditions
	} = useFinancialIndicatedConditions();

	const {
		loading: simulationUpdateLoading,
		error: simulationUpdateError,
		validationErrors: simulationUpdateValidationErrors,
		updateSimulationById
	} = useSimulationUpdate();

	const {
		loading: simulationCreateLoading,
		error: simulationCreateError,
		validationErrors: simulationCreateValidationErrors,
		createSimulation
	} = useSimulation();

	const isSimulationPageLoading = (isViewMode || isEditMode) && simulationLoading;

	const financialInstallmentsLoadingMessage =
		financialInstallmentsLoading && financialInstallmentsRetryCount > 1
			? `Carregando parcelas disponíveis tentativa ${financialInstallmentsRetryCount} de ${financialInstallmentsMaxRetries}`
			: "Carregando parcelas disponíveis...";

	const calculationData = isViewMode ? simulation : calculationResult;

	const shouldShowCalculationResult = isViewMode
		? Boolean(simulation)
		: Boolean(calculationResult);

	// Sempre abre a tela no topo ao entrar na página de simulação.
	useEffect(() => {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: "auto"
		});
	}, []);

	// Após calcular/recalcular, move o foco para a área dos resultados.
	useEffect(() => {
		if (!shouldFocusCalculationRef.current) {
			return;
		}

		if (!shouldShowCalculationResult) {
			return;
		}

		shouldFocusCalculationRef.current = false;

		if (calculationSectionRef.current) {
			calculationSectionRef.current.scrollIntoView({
				behavior: "smooth",
				block: "start"
			});

			calculationSectionRef.current.focus();
		}
	}, [shouldShowCalculationResult, calculationData]);

	// Verifica se o usuário tem acesso ao módulo Simulador.
	useEffect(() => {
		setHasModuleAccess(TokenUtils.hasModuloSimulador());
	}, []);

	// Mantém o modo da tela sem expor na URL e preserva após refresh.
	useEffect(() => {
		saveSimulationMode({
			simulationId: storageSimulationId,
			mode
		});
	}, [storageSimulationId, mode]);

	// Restaura os dados ao voltar da tela de envio pelo botão Refazer Simulação.
	useEffect(() => {
		if (restoredSimulationStateRef.current) {
			return;
		}

		const restoreSimulationState = location.state?.restoreSimulationState;

		if (!restoreSimulationState) {
			return;
		}

		restoredSimulationStateRef.current = true;
		setIsRestoringSimulationState(true);
		setRestoredSimulationData(restoreSimulationState.simulationFormData || null);

		setFormValues(
			getPersistableSimulationFormValues(
				restoreSimulationState.simulationFormData
			)
		);

		setCalculationResult(null);
		setFinancialConditionParams(restoreSimulationState.financialConditionParams || null);
		setIndicatedConditions(restoreSimulationState.indicatedConditions || null);
		setSelectedPaymentConditionId(restoreSimulationState.selectedPaymentConditionId || null);
	}, [location.state]);

	// Após limpar os campos por alteração de CNPJ, volta a preservar os campos normalmente.
	useEffect(() => {
		if (!shouldClearFieldsAfterCnpjChange) {
			return;
		}

		const timeoutId = setTimeout(() => {
			setShouldClearFieldsAfterCnpjChange(false);
		}, 0);

		return () => clearTimeout(timeoutId);
	}, [shouldClearFieldsAfterCnpjChange]);


	// Busca condições indicadas quando os parâmetros financeiros são atualizados.
	// O valor do equipamento atualiza esses parâmetros no blur; parcelas e CNPJ
	// podem atualizar esses parâmetros após mudança/consulta.
	// No restore, a chamada é bloqueada até o usuário alterar esses campos.
	useEffect(() => {
		if (isViewMode || isRestoringSimulationState) {
			return;
		}

		if (!financialConditionParams) {
			return;
		}

		const equipmentValue = getCurrencyNumber(financialConditionParams.equipmentValue);
		const installments = financialConditionParams.installments;

		const clientDocument =
			financialConditionParams.clientDocument ||
			formValues?.clientDocument ||
			restoredSimulationData?.clientDocument ||
			simulation?.clientDocument ||
			"";

		if (!equipmentValue || !installments || !clientDocument) {
			setIndicatedConditions(null);
			setIsRefreshingPaymentConditions(false);
			lastIndicatedConditionsParamsRef.current = null;
			return;
		}

		const requestKey = `${clientDocument}-${equipmentValue}-${installments}`;

		if (lastIndicatedConditionsParamsRef.current === requestKey) {
			return;
		}

		setIndicatedConditions(null);

		let shouldIgnoreResponse = false;

		const fetchIndicatedConditions = async () => {
			lastIndicatedConditionsParamsRef.current = requestKey;

			const result = await fetchFinancialIndicatedConditions(
				equipmentValue,
				installments,
				clientDocument
			);

			if (shouldIgnoreResponse) {
				return;
			}

			if (!result?.ok) {
				setIndicatedConditions(null);
				setIsRefreshingPaymentConditions(false);

				setFormValues(previous => ({
					...previous,
					paymentConditions: ""
				}));

				return;
			}

			setIndicatedConditions(result.indicatedConditions);
			setIsRefreshingPaymentConditions(false);
		};

		fetchIndicatedConditions();

		return () => {
			shouldIgnoreResponse = true;
		};
	}, [
		isViewMode,
		isRestoringSimulationState,
		financialConditionParams
	]);

	// Dispara a busca das condições de pagamento após o CNPJ ser encontrado.
	// Esse fluxo cobre tanto a resposta da API quanto o retorno vindo do cache,
	// porque ambos passam pelo handleCnpjComplete.
	const reloadPaymentConditionsAfterClientSearch = cnpj => {
		const equipmentValue =
			formValues?.equipmentValue ??
			financialConditionParams?.equipmentValue ??
			"";

		const installments =
			formValues?.installments ??
			financialConditionParams?.installments ??
			"";

		if (!getCurrencyNumber(equipmentValue) || !installments) {
			return;
		}

		setIsRestoringSimulationState(false);
		setIsRefreshingPaymentConditions(true);

		setIndicatedConditions(null);
		setSelectedPaymentConditionId(null);

		lastIndicatedConditionsParamsRef.current = null;

		setFinancialConditionParams({
			equipmentValue,
			installments,
			clientDocument: cnpj
		});
	};

	// Busca os dados do cliente quando o CNPJ é completado.
	// Se o cliente for encontrado, também tenta buscar as condições de pagamento
	// caso valor do equipamento e parcelas já tenham sido preenchidos.
	const handleCnpjComplete = async cnpj => {
		const result = await searchClientByCnpj(cnpj);

		if (!result?.ok) {
			await showCommonAlert({
				title: "Aviso",
				message: result?.error || "CNPJ não encontrado.",
				icon: "warning",
				customClass: {
					confirmButton: "alert-form-button"
				}
			});

			return result;
		}

		reloadPaymentConditionsAfterClientSearch(cnpj);

		return result;
	};

	const handleSimulationFieldChange = ({
		name,
		value,
		values,
		previousValues
	}) => {
		const shouldClearPaymentConditions = [
			"equipmentValue",
			"installments"
		].includes(name);

		// Parcelas continuam disparando a busca no change.
		// Valor do equipamento só dispara no blur.
		const shouldReloadPaymentConditions = name === "installments";

		const hasFieldChanged = previousValues?.[name] !== value;

		if (name === "equipmentValue" && hasFieldChanged) {
			setClearedValidationErrors(previous => ({
				...previous,
				equipmentValue: true
			}));
		}

		if (
			name === "clientDocument" &&
			hasFieldChanged &&
			previousValues?.clientDocument
		) {
			setShouldClearFieldsAfterCnpjChange(true);

			setFormValues({
				clientDocument: value,
				clientName: "",
				equipmentName: "",
				equipmentValue: "",
				installments: "",
				paymentConditions: "",
				buyback: false,
				buybackEquipment: "",
				buybackValue: ""
			});

			setCalculationResult(null);
			setIndicatedConditions(null);
			setFinancialConditionParams(null);
			setSelectedPaymentConditionId(null);
			setIsRefreshingPaymentConditions(false);
			lastIndicatedConditionsParamsRef.current = null;

			return;
		}

		const persistableValues = getPersistableSimulationFormValues(values) || {};

		const nextValues = {
			...formValues,
			...persistableValues,
			[name]: value,
			...(shouldClearPaymentConditions && hasFieldChanged
				? {
					paymentConditions: ""
				}
				: {})
		};

		setFormValues(nextValues);

		if (!isViewMode && hasFieldChanged) {
			setCalculationResult(null);
		}

		if (shouldClearPaymentConditions && hasFieldChanged) {
			setSelectedPaymentConditionId(null);
		}

		if (name === "paymentConditions") {
			setSelectedPaymentConditionId(value);
			return;
		}

		if (!shouldReloadPaymentConditions || !hasFieldChanged) {
			return;
		}

		setIsRestoringSimulationState(false);
		setIsRefreshingPaymentConditions(true);
		setIndicatedConditions(null);
		setSelectedPaymentConditionId(null);

		setFinancialConditionParams({
			equipmentValue: nextValues.equipmentValue,
			installments: nextValues.installments,
			clientDocument: nextValues.clientDocument
		});
	};

	const handleSimulationFieldBlur = ({
		name,
		values
	}) => {
		if (name !== "equipmentValue") {
			return;
		}

		const equipmentValue =
			values?.equipmentValue ??
			formValues?.equipmentValue ??
			"";

		const installments =
			values?.installments ??
			formValues?.installments ??
			financialConditionParams?.installments ??
			"";

		const clientDocument =
			values?.clientDocument ??
			formValues?.clientDocument ??
			financialConditionParams?.clientDocument ??
			restoredSimulationData?.clientDocument ??
			simulation?.clientDocument ??
			"";

		if (!getCurrencyNumber(equipmentValue) || !installments || !clientDocument) {
			return;
		}

		setIsRestoringSimulationState(false);
		setIsRefreshingPaymentConditions(true);
		setIndicatedConditions(null);
		setSelectedPaymentConditionId(null);

		setFinancialConditionParams({
			equipmentValue,
			installments,
			clientDocument
		});
	};
	
	const handleCalculateSubmitDisabled = values => {
		const currentValues = {
			...values,
			...formValues
		};

		return isCalculateSubmitDisabled({
			values: currentValues,
			financialInstallmentsLoading,
			financialInstallmentsError
		});
	};

	const handleCalculateSubmit = async values => {
		if (isViewMode) {
			return;
		}

		try {
			setClearedValidationErrors({});

			setSimulationActionLoadingMessage(
				isEditMode ? "Recalculando simulação..." : "Calculando simulação..."
			);

			shouldFocusCalculationRef.current = true;

			const payloadValues = {
				...values,
				minimumInitialPayment: simulationFormData.minimumInitialPayment,
				maximumInitialPayment: simulationFormData.maximumInitialPayment,
				downPaymentPercentage: simulationFormData.downPaymentPercentage,
				initialPayment: simulationFormData.initialPayment,
				calculatedInstallments: simulationFormData.calculatedInstallments || []
			};

			const payloadData = {
				values: payloadValues,
				paymentCondition: activePaymentCondition || baseSimulationData?.raw?.condicaoComercial,
				installments: baseSimulationData?.raw?.parcelas || [],
				periodNetRevenue: baseSimulationData?.raw?.dadosCliente?.faturamentoLiquidoNoPeriodo || 0,
				minimumInitialPercentual: simulationFormData.minimumInitialPercentual,
				maximumInitialPercentual: simulationFormData.maximumInitialPercentual,
				buyback: payloadValues.buyback
					? {
						nomeEquipamento: payloadValues.buybackEquipment || "",
						valor: getCurrencyNumber(payloadValues.buybackValue)
					}
					: null
			};

			const shouldUpdateSimulation = isEditMode || Boolean(createdSimulationId);

			const result = shouldUpdateSimulation
				? await updateSimulationById(simulationId, payloadData)
				: await createSimulation(payloadData);

			if (!result?.ok) {
				shouldFocusCalculationRef.current = false;
				setSimulationActionLoadingMessage("");

				if (result?.isValidationError) {
					await showCommonAlert({
						message: result.error,
						fallbackMessage: "Não foi possível calcular a simulação.",
						customClass: {
							confirmButton: "alert-form-button"
						}
					});
				}

				return;
			}

			const resultSimulationId = result?.simulation?.id;

			if (!isEditMode && resultSimulationId) {
				setCreatedSimulationId(resultSimulationId);
			}

			setFormValues(getPersistableSimulationFormValues(values));
			setCalculationResult(result.simulation);

			if ((result?.simulation?.basedOnMinimumValues) && (!result?.simulation?.clientNotfound)) {
				setSimulationActionLoadingMessage("");

				await showClientWithoutSalesHistoryAlert({
					title: "Cliente com faturamento abaixo da baseline",
					subtitle: "Como o faturamento do cliente está abaixo do faturamento mínimo previsto no programa Pacto, suas metas serão calculadas a partir do valor base de {averageMonthlySales}.", 
					averageMonthlySales: result?.simulation?.minimumAverageMonthlySales
				});
			} else if (result?.simulation?.clientNotfound) {
				setSimulationActionLoadingMessage("");

				await showClientWithoutSalesHistoryAlert({
					title: "Cliente sem Histórico de Vendas",
					subtitle: "Este cliente ainda não possui histórico de vendas registrado, por isso, suas metas serão calculadas a partir do valor base de {averageMonthlySales}.", 
					averageMonthlySales: result?.simulation?.minimumAverageMonthlySales
				});
			}
		} finally {
			setSimulationActionLoadingMessage("");
		}
	};

	const handleFinalButtonClick = () => {
		if (isViewMode) {
			clearSimulationMode(simulationId || storageSimulationId);
			history.goBack();
			return;
		}

		const currentSimulationFormData = {
			...simulationFormData,
			id: simulationId,
			raw: baseSimulationData?.raw,
			clientDocument: formValues?.clientDocument ?? simulationFormData?.clientDocument ?? "",
			clientName: formValues?.clientName ?? simulationFormData?.clientName ?? "",
			equipmentName: formValues?.equipmentName ?? simulationFormData?.equipmentName ?? "",
			equipmentValue: simulationFormData?.equipmentValue ?? "",
			installments: formValues?.installments ?? simulationFormData?.installments ?? "",
			paymentConditions: selectedPaymentConditionId ||
				simulationFormData?.paymentConditions ||
				"",
			buyback: formValues?.buyback ?? simulationFormData?.buyback ?? false,
			buybackEquipment: formValues?.buybackEquipment ?? simulationFormData?.buybackEquipment ?? "",
			buybackValue: formValues?.buybackValue ?? simulationFormData?.buybackValue ?? ""
		};

		const envioDadosSimulation = {
			...calculationData,

			clientDocument: currentSimulationFormData.clientDocument || "",
			clientName: currentSimulationFormData.clientName || "",

			equipmentName: currentSimulationFormData.equipmentName || "",
			equipmentValue: currentSimulationFormData.equipmentValue || "",
			installments: currentSimulationFormData.installments || "",

			paymentConditions: currentSimulationFormData.paymentConditions || "",
			paymentConditionName: activePaymentCondition?.nome ||
				baseSimulationData?.raw?.condicaoComercial?.nome ||
				"",

			downPaymentPercentage: currentSimulationFormData.downPaymentPercentage || "",
			initialPayment: currentSimulationFormData.initialPayment || "",
			minimumInitialPayment: currentSimulationFormData.minimumInitialPayment || "",
			maximumInitialPayment: currentSimulationFormData.maximumInitialPayment || "",

			calculatedInstallments: currentSimulationFormData.calculatedInstallments || []
		};

		saveSimulationMode({
			simulationId: simulationId || storageSimulationId,
			mode
		});

		history.push("/pacto-simulador/envio-dados", {
			calculationData: envioDadosSimulation,
			returnState: {
				returnPath: location.pathname,
				simulationId: simulationId || storageSimulationId,
				simulationFormData: currentSimulationFormData,
				calculationResult,
				financialConditionParams,
				indicatedConditions,
				selectedPaymentConditionId,
				mode
			}
		});
	};

	const handleBackToSimulator = () => {
		clearSimulationMode(simulationId || storageSimulationId);
		clearSimulationMode("create");

		history.push("/pacto-simulador");
	};

	if (!context.userSession) {
		return (
			<PageStateMessage title="Carregando..." />
		);
	}

	if (!hasModuleAccess) {
		return (
			<PageStateMessage
				title="Acesso Negado"
				description="Você não possui acesso ao módulo Simulador."
			/>
		);
	}

	const baseSimulationData = restoredSimulationData || simulation;

	const hasUserChangedFinancialFields = Boolean(financialConditionParams);

	const shouldIgnoreBasePaymentConditions =
		isEditMode &&
		!hasUserChangedFinancialFields;

	const simulationId =
		createdSimulationId ||
		baseSimulationData?.id ||
		routeSimulationId;

	const innerPageElements = {
		backLabel: [{ label: "Simulações Realizadas" }]
	};

	const currentEquipmentValue = financialConditionParams?.equipmentValue !== undefined
		? getCurrencyNumber(financialConditionParams.equipmentValue)
		: formValues?.equipmentValue !== undefined
			? getCurrencyNumber(formValues.equipmentValue)
			: baseSimulationData?.equipmentValue ?? "";

	const shouldUseBasePaymentCondition =
		!indicatedConditions &&
		!shouldIgnoreBasePaymentConditions;

	const activePaymentConditionId =
		selectedPaymentConditionId ||
		(shouldUseBasePaymentCondition
			? baseSimulationData?.paymentConditions
			: "") ||
		"";

	const activePaymentCondition = (
		indicatedConditions?.paymentConditionData || []
	).find(condition => String(condition.id) === String(activePaymentConditionId));

	const activeIndicatedConditions = indicatedConditions
		? {
			...indicatedConditions,
			paymentConditions: activePaymentConditionId,
			downPaymentPercentage: activePaymentCondition
				? activePaymentCondition.percentualEntrada
				: ""
		}
		: null;

	const initialPaymentFields = getInitialPaymentFields({
		equipmentValue: currentEquipmentValue,
		indicatedConditions: activeIndicatedConditions,
		simulation: baseSimulationData
	});

	const activeCalculatedInstallments = activePaymentCondition
		? formatCalculatedInstallmentsByPaymentCondition(activePaymentCondition)
		: baseSimulationData?.calculatedInstallments || [];


	const shouldClearIndicatedFields =
		shouldIgnoreBasePaymentConditions ||
		(
			hasUserChangedFinancialFields &&
			(isRefreshingPaymentConditions || indicatedConditionsError)
		);

	const indicatedConditionFields = shouldClearIndicatedFields
		? {
			paymentConditions: "",
			paymentConditionOptions: [],
			minimumInitialPercentual: "",
			maximumInitialPercentual: "",
			downPaymentPercentage: "",
			minimumInitialPayment: "",
			maximumInitialPayment: "",
			initialPayment: "",
			calculatedInstallments: []
		}
		: hasUserChangedFinancialFields
			? {
				paymentConditions: activeIndicatedConditions?.paymentConditions ?? "",
				paymentConditionOptions: activeIndicatedConditions?.paymentConditionOptions ?? [],
				minimumInitialPercentual: activeIndicatedConditions?.minimumInitialPercentual ?? "",
				maximumInitialPercentual: activeIndicatedConditions?.maximumInitialPercentual ?? "",
				downPaymentPercentage: activeIndicatedConditions?.downPaymentPercentage ?? "",
				minimumInitialPayment: initialPaymentFields.minimumInitialPayment,
				maximumInitialPayment: initialPaymentFields.maximumInitialPayment,
				initialPayment: initialPaymentFields.initialPayment
			}
			: {
				paymentConditions: baseSimulationData?.paymentConditions ?? "",
				paymentConditionOptions: baseSimulationData?.paymentConditionOptions ?? [],
				minimumInitialPercentual: baseSimulationData?.minimumInitialPercentual ?? "",
				maximumInitialPercentual: baseSimulationData?.maximumInitialPercentual ?? "",
				downPaymentPercentage: baseSimulationData?.downPaymentPercentage ?? "",
				minimumInitialPayment: baseSimulationData?.minimumInitialPayment ?? "",
				maximumInitialPayment: baseSimulationData?.maximumInitialPayment ?? "",
				initialPayment: baseSimulationData?.initialPayment ?? ""
			};

	const currentInstallments =
		financialConditionParams?.installments ??
		formValues?.installments ??
		(isViewMode || isRestoringSimulationState
			? baseSimulationData?.installments
			: "") ??
		"";

	const currentBuybackValue = formValues?.buybackValue !== undefined
		? getCurrencyNumber(formValues.buybackValue)
		: baseSimulationData?.buybackValue ?? "";

	const buybackValueInstallment = calculateBuybackInstallmentValue({
		buybackValue: currentBuybackValue,
		installments: currentInstallments
	});

	const simulationFormData = {
		...baseSimulationData,
		...indicatedConditionFields,

		clientDocument: formValues?.clientDocument ?? baseSimulationData?.clientDocument ?? "",
		clientName: formValues?.clientName ?? baseSimulationData?.clientName ?? "",

		equipmentName: formValues?.equipmentName ?? baseSimulationData?.equipmentName ?? "",
		equipmentValue: currentEquipmentValue,
		installments: currentInstallments,

		paymentConditions: shouldClearIndicatedFields
			? ""
			: activePaymentConditionId,

		calculatedInstallments: shouldClearIndicatedFields
			? []
			: activeCalculatedInstallments,

		buyback: formValues?.buyback ?? baseSimulationData?.buyback ?? false,
		buybackEquipment: formValues?.buybackEquipment ?? baseSimulationData?.buybackEquipment ?? "",
		buybackValue: currentBuybackValue,
		buybackValueInstallment
	};

	const simulationFormConfig = inputsConfig({
		onCnpjComplete: handleCnpjComplete,
		simulation: simulationFormData,
		shouldSearchCnpj,
		isViewMode,
		isEditMode,
		isRestoringSimulationState,
		financialInstallments,
		paymentConditionsLoading: indicatedConditionsLoading || isRefreshingPaymentConditions
	});

	const calculationSummaryGroups = shouldShowCalculationResult
		? summaryConfig({
			simulation: calculationData
		}).filter(group => {
			return ["clientProfile", "variluxGoals", "growthGoals"].includes(group.key);
		})
		: [];

	const primaryButtonLabel = isEditMode ? "Recalcular" : "Calcular";
	const finalButtonLabel = isViewMode ? "Voltar" : "Próximo";

	const finalButtonStartIcon = isViewMode ? "arrowLeft" : null;
	const finalButtonEndIcon = isViewMode ? null : "arrowRight";

	const shouldPreservePaymentCondition =
		selectedPaymentConditionId !== null &&
		!isRefreshingPaymentConditions;

	const shouldPreserveUserChangedFields = Boolean(formValues);

	const preserveFieldsOnUpdate = shouldClearFieldsAfterCnpjChange
		? ["clientDocument"]
		: [
			...(mode === "create" ? ["clientDocument", "clientName"] : []),
			...(shouldPreserveUserChangedFields
				? [
					"equipmentName",
					"equipmentValue",
					"installments",
					"buyback",
					"buybackEquipment",
					"buybackValue"
				]
				: []),
			...(shouldPreservePaymentCondition ? ["paymentConditions"] : [])
		];

	const simulationValidationErrors = removeClearedValidationErrors({
		errors: {
			...validationErrors,
			...simulationCreateValidationErrors,
			...simulationUpdateValidationErrors,
			...(financialInstallmentsError && {
				installments: financialInstallmentsError
			}),
			...(indicatedConditionsError && {
				paymentConditions: indicatedConditionsError
			})
		},
		clearedErrors: clearedValidationErrors
	});

	return (
		<InnerPageStructure innerPageElements={innerPageElements}>
			<PageLoading
				actionMessage={simulationActionLoadingMessage}
				states={[
					{
						loading: isSimulationPageLoading,
						message: "Carregando dados da simulação..."
					},
					{
						loading: financialInstallmentsLoading,
						message: financialInstallmentsLoadingMessage
					},
					{
						loading: clientByCnpjLoading,
						message: "Buscando dados do cliente..."
					},
					{
						loading: indicatedConditionsLoading,
						message: "Buscando condições de pagamento..."
					},
					{
						loading: simulationCreateLoading,
						message: "Calculando simulação..."
					},
					{
						loading: simulationUpdateLoading,
						message: "Recalculando simulação..."
					}
				]}
			/>
			<SimulationErrorAlerts
				simulationError={error}
				financialInstallmentsError={financialInstallmentsError}
				indicatedConditionsError={indicatedConditionsError}
				simulationCreateError={simulationCreateError}
				simulationUpdateError={simulationUpdateError}
			/>
			<PageSteps
				steps={simulationSteps}
				currentStep={1}
				className="simulacao__steps"
			/>

			<FormGeneric
				key={`simulation-form-${mode}-${baseSimulationData?.id || "create"}`}
				formFields={simulationFormConfig}
				message={isViewMode ? null : primaryButtonLabel}
				onSubmit={handleCalculateSubmit}
				errors={simulationValidationErrors}
				isSubmitDisabled={handleCalculateSubmitDisabled}
				onFieldChange={handleSimulationFieldChange}
				onFieldBlur={handleSimulationFieldBlur}
				preserveFieldsOnUpdate={preserveFieldsOnUpdate}
				preserveDisabledFieldsOnUpdate={mode === "create" ? ["clientName"] : []}
			/>

			{shouldShowCalculationResult && (
				<div
					ref={calculationSectionRef}
					className="simulacao__calculation-section form-generic"
					tabIndex={-1}
				>
					<SimulationSummary groups={calculationSummaryGroups} />
				</div>
			)}

			{(!isViewMode || shouldShowCalculationResult) && (
				<div
					className={[
						"simulacao__send-action form-generic",
						shouldShowCalculationResult
							? "simulacao__send-action--between"
							: "simulacao__send-action--before-result"
					].filter(Boolean).join(" ")}
				>
					{!isViewMode && (
						<AppButton
							type="button"
							onClick={handleBackToSimulator}
							startIcon="arrowLeft"
							iconLayout="edge"
							className="simulacao__action-button simulacao__action-button--outline"
							style={{
								width: "200px"
							}}
						>
							Voltar
						</AppButton>
					)}

					{shouldShowCalculationResult && (
						<AppButton
							type="button"
							onClick={handleFinalButtonClick}
							startIcon={finalButtonStartIcon}
							endIcon={finalButtonEndIcon}
							iconLayout="edge"
							className="simulacao__action-button"
							style={{
								width: "200px"
							}}
						>
							{finalButtonLabel}
						</AppButton>
					)}
				</div>
			)}
		</InnerPageStructure>
	);
};

export default Simulacao;